import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2 } from 'lucide-react';
import { autoBiddingApi } from '@/api/client';
import { downloadTextFile, rowsToCsv } from '@/api/csvExport';
import { TableSearchInput } from '@/components/TableSearchInput';
import { WorkspaceAutoBiddingModal } from '@/components/workspace/WorkspaceAutoBiddingModal';
import { filterRowsByTableSearch } from '@/utils/tableSearch';
import {
  autoBiddingEntityLabel,
  autoBiddingFrequencyLabel,
  autoBiddingHistorySearchValues,
  autoBiddingStrategyLabel,
  emptyAutoBiddingPreview,
  flattenAutoBiddingHistory,
  formatAutoBiddingAcos,
  parseAutoBiddingPreview,
  parseAutoBiddingRunLogs,
  parseAutoBiddingSettings,
  supportsAutoBidding,
  type AutoBiddingHistoryRow,
  type AutoBiddingPreview,
  type AutoBiddingPreviewChange,
  type AutoBiddingPreviewHarvest,
  type AutoBiddingRunLog,
  type AutoBiddingSettings,
} from '@/utils/autoBidding';

type AutoBiddingProduct = 'SP' | 'SB';

type PanelProps = {
  profileId: string;
  campaignId: string;
  campaignName: string;
  adProduct: string;
};

type ViewProps = {
  settings: AutoBiddingSettings | null;
  settingsLoading: boolean;
  settingsError: string | null;
  onConfigure: () => void;
  preview: AutoBiddingPreview | null;
  previewLoading: boolean;
  previewError: string | null;
  logs: AutoBiddingRunLog[];
  logsLoading: boolean;
  logsError: string | null;
};

function formatBid(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return 'N/A';
  return value.toFixed(2);
}

function formatRunTime(iso: string | null | undefined): string {
  if (!iso) return 'N/A';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function yesNo(value: boolean): string {
  return value ? 'Yes' : 'No';
}

function configValue(value: string | null | undefined): string {
  return value && value.trim() ? value : 'Not set';
}

function statusClass(status: string): string {
  const lower = status.toLowerCase();
  if (lower === 'applied' || lower === 'harvested' || lower === 'success') {
    return 'bg-success-subtle text-success-ink';
  }
  if (lower === 'failed') return 'bg-error-subtle text-error-ink';
  return 'bg-warning-subtle text-warning-ink';
}

function previewRows(
  preview: AutoBiddingPreview
): Array<AutoBiddingPreviewChange | (AutoBiddingPreviewHarvest & { previousBid?: null })> {
  return [...preview.changes, ...preview.harvests];
}

function previewEntity(row: AutoBiddingPreviewChange | AutoBiddingPreviewHarvest): string {
  if ('id' in row) return row.text || row.id;
  return row.text;
}

function previewKind(row: AutoBiddingPreviewChange | AutoBiddingPreviewHarvest): string {
  if ('id' in row) return autoBiddingEntityLabel(row.kind);
  return 'Harvest';
}

function previewCurrentBid(row: AutoBiddingPreviewChange | AutoBiddingPreviewHarvest): number | null {
  return 'previousBid' in row ? row.previousBid : null;
}

export function AutoBiddingActivityPanelView({
  settings,
  settingsLoading,
  settingsError,
  onConfigure,
  preview,
  previewLoading,
  previewError,
  logs,
  logsLoading,
  logsError,
}: ViewProps) {
  const [historySearch, setHistorySearch] = useState('');
  const [downloading, setDownloading] = useState(false);

  const historyRows = useMemo(() => flattenAutoBiddingHistory(logs), [logs]);
  const visibleHistory = useMemo(
    () => filterRowsByTableSearch(historyRows, historySearch, autoBiddingHistorySearchValues),
    [historyRows, historySearch]
  );

  const headerBits = useMemo(() => {
    if (!preview) return [];
    const bits: string[] = [];
    const strategy = autoBiddingStrategyLabel(preview.strategy);
    if (strategy) bits.push(strategy);
    const target = formatAutoBiddingAcos(preview.targetAcos);
    if (target) bits.push(`Target ACOS ${target}`);
    if (preview.nextRunAt) bits.push(`Next run ${formatRunTime(preview.nextRunAt)}`);
    return bits;
  }, [preview]);

  const planned = preview ? previewRows(preview) : [];

  function handleDownloadCsv() {
    if (visibleHistory.length === 0) return;
    setDownloading(true);
    try {
      const headers = ['Run time', 'Entity', 'Type', 'Previous bid', 'New bid', 'Reason', 'Status'];
      const csv = rowsToCsv(
        headers,
        visibleHistory.map((row: AutoBiddingHistoryRow) => [
          row.runAt ? formatRunTime(row.runAt) : '',
          row.entity,
          row.type,
          row.previousBid,
          row.newBid,
          row.reason,
          row.status,
        ])
      );
      downloadTextFile('auto-bidding-history.csv', csv);
    } finally {
      setDownloading(false);
    }
  }

  let previewEmpty: string | null = null;
  if (!previewLoading && !previewError) {
    if (!preview?.enabled) previewEmpty = 'Auto bidding is off';
    else if (preview.error) previewEmpty = preview.error;
    else if (planned.length === 0) previewEmpty = 'No bid changes planned for the next run';
  }

  const settingsEnabled = settings?.enabled === true;
  const targetAcos = formatAutoBiddingAcos(settings?.targetAcos);
  const configRows = settingsEnabled
    ? [
        ['Strategy', configValue(autoBiddingStrategyLabel(settings?.strategy))],
        ['Target ACOS', configValue(targetAcos)],
        ['Bidding frequency', configValue(autoBiddingFrequencyLabel(settings?.biddingFrequency))],
        ['Max bid', settings?.maxBid != null ? formatBid(settings.maxBid) : 'Not set'],
        ['Automatically add targets', yesNo(settings?.autoAddTargets === true)],
        ['Reduce Campaign Out of budget', yesNo(settings?.reduceOutOfBudget === true)],
        ['Next run', formatRunTime(settings?.nextRunAt)],
      ]
    : [];

  return (
    <div className="space-y-4">
      <div className="bg-surface rounded-xl shadow border border-border p-6">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Configuration</h3>
            <p className="text-xs text-foreground-subtle mt-0.5">
              Strategy and limits used by Auto Bidding.
            </p>
          </div>
          {!settingsLoading ? (
            <button
              type="button"
              onClick={onConfigure}
              className="text-sm font-medium text-brand hover:underline"
            >
              {settingsEnabled ? 'Edit' : 'Enable'}
            </button>
          ) : null}
        </div>
        {settingsLoading ? (
          <div className="text-sm text-foreground-subtle">Loading configuration…</div>
        ) : settingsError ? (
          <div className="text-sm text-error">{settingsError}</div>
        ) : !settingsEnabled ? (
          <div className="text-sm text-foreground-subtle">Auto bidding is off</div>
        ) : (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {configRows.map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs text-foreground-muted">{label}</dt>
                <dd className="text-foreground">{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      <div className="bg-surface rounded-xl shadow border border-border p-6">
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-foreground">Next run preview</h3>
          <p className="text-xs text-foreground-subtle mt-0.5">
            Recommended bid changes using the same math as the Auto Bidding engine. Changes apply on the next scheduled run.
          </p>
          {headerBits.length > 0 ? (
            <p className="text-sm text-foreground-muted mt-2">{headerBits.join(' · ')}</p>
          ) : null}
          {preview?.outOfBudget ? (
            <p className="text-sm text-warning-ink mt-2">
              Campaign is out of budget — bid increases are skipped.
            </p>
          ) : null}
        </div>
        {previewLoading ? (
          <div className="text-sm text-foreground-subtle">Loading next-run preview…</div>
        ) : previewError ? (
          <div className="text-sm text-error">{previewError}</div>
        ) : previewEmpty ? (
          <div className="text-sm text-foreground-subtle">{previewEmpty}</div>
        ) : (
          <div className="max-h-56 overflow-x-auto overflow-y-auto rounded-xl border border-border">
            <table className="min-w-full text-sm table-fixed">
              <thead>
                <tr className="bg-page text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
                  <th className="px-3 py-2">Entity</th>
                  <th className="px-3 py-2 whitespace-nowrap">Type</th>
                  <th className="px-3 py-2 whitespace-nowrap">Current bid</th>
                  <th className="px-3 py-2 whitespace-nowrap">Proposed bid</th>
                  <th className="px-3 py-2">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {planned.map((row, index) => (
                  <tr key={`${previewKind(row)}-${previewEntity(row)}-${index}`} className="bg-surface">
                    <td className="px-3 py-2 text-foreground">{previewEntity(row)}</td>
                    <td className="px-3 py-2 text-foreground-muted whitespace-nowrap">{previewKind(row)}</td>
                    <td className="px-3 py-2 text-foreground-muted whitespace-nowrap">
                      {formatBid(previewCurrentBid(row))}
                    </td>
                    <td className="px-3 py-2 text-foreground whitespace-nowrap">{formatBid(row.proposedBid)}</td>
                    <td className="px-3 py-2 text-foreground-muted">{row.reason || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-surface rounded-xl shadow border border-border p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Bid change history</h3>
            <p className="text-xs text-foreground-subtle mt-0.5">
              Applied Auto Bidding changes (most recent first).
            </p>
          </div>
          {!logsLoading && !logsError && historyRows.length > 0 ? (
            <div className="flex items-center gap-2">
              <TableSearchInput
                value={historySearch}
                onChange={setHistorySearch}
                placeholder="Search bid history…"
                id="auto-bidding-history-search"
              />
              <button
                type="button"
                onClick={handleDownloadCsv}
                disabled={downloading || visibleHistory.length === 0}
                aria-busy={downloading}
                aria-label={downloading ? 'Preparing CSV download' : 'Download bid change history as CSV'}
                className="inline-flex h-[2.375rem] shrink-0 items-center gap-1.5 rounded-lg bg-brand px-3 text-sm font-medium text-brand-foreground hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-80"
              >
                {downloading ? (
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
                ) : (
                  <Download className="h-3.5 w-3.5 shrink-0" aria-hidden />
                )}
                <span className="whitespace-nowrap">{downloading ? 'Preparing…' : 'Download CSV'}</span>
              </button>
            </div>
          ) : null}
        </div>
        {logsLoading ? (
          <div className="text-sm text-foreground-subtle">Loading bid change history…</div>
        ) : logsError ? (
          <div className="text-sm text-error">{logsError}</div>
        ) : historyRows.length === 0 ? (
          <div className="text-sm text-foreground-subtle">No bid changes recorded yet</div>
        ) : visibleHistory.length === 0 ? (
          <div className="text-sm text-foreground-subtle">No rows match your search.</div>
        ) : (
          <>
            {historySearch.trim() ? (
              <p className="text-sm text-foreground-subtle mb-2">
                Showing {visibleHistory.length} of {historyRows.length} change(s)
              </p>
            ) : null}
            <div className="max-h-56 overflow-x-auto overflow-y-auto rounded-xl border border-border">
              <table className="min-w-full text-sm table-fixed">
                <thead>
                  <tr className="bg-page text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
                    <th className="px-3 py-2 whitespace-nowrap">Run time</th>
                    <th className="px-3 py-2">Entity</th>
                    <th className="px-3 py-2 whitespace-nowrap">Type</th>
                    <th className="px-3 py-2 whitespace-nowrap">Previous bid</th>
                    <th className="px-3 py-2 whitespace-nowrap">New bid</th>
                    <th className="px-3 py-2">Reason</th>
                    <th className="px-3 py-2 whitespace-nowrap">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {visibleHistory.map((row) => (
                    <tr key={row.id} className="bg-surface hover:bg-page/80">
                      <td className="px-3 py-2 text-foreground whitespace-nowrap">{formatRunTime(row.runAt)}</td>
                      <td className="px-3 py-2 text-foreground">{row.entity}</td>
                      <td className="px-3 py-2 text-foreground-muted whitespace-nowrap">{row.type}</td>
                      <td className="px-3 py-2 text-foreground-muted whitespace-nowrap">{formatBid(row.previousBid)}</td>
                      <td className="px-3 py-2 text-foreground whitespace-nowrap">{formatBid(row.newBid)}</td>
                      <td className="px-3 py-2 text-foreground-muted max-w-0">
                        <span className="block truncate" title={row.reason || undefined}>
                          {row.reason || 'N/A'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusClass(row.status)}`}
                        >
                          {row.status || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function AutoBiddingActivityPanel({
  profileId,
  campaignId,
  campaignName,
  adProduct,
}: PanelProps) {
  if (!supportsAutoBidding(adProduct)) return null;
  const product = adProduct as AutoBiddingProduct;

  return (
    <AutoBiddingActivityLoader
      profileId={profileId}
      campaignId={campaignId}
      campaignName={campaignName}
      adProduct={product}
    />
  );
}

function AutoBiddingActivityLoader({
  profileId,
  campaignId,
  campaignName,
  adProduct,
}: {
  profileId: string;
  campaignId: string;
  campaignName: string;
  adProduct: AutoBiddingProduct;
}) {
  const queryClient = useQueryClient();
  const [configOpen, setConfigOpen] = useState(false);
  const settingsQuery = useQuery({
    queryKey: ['auto-bidding-settings', profileId, campaignId, adProduct],
    queryFn: async () => {
      const res = await autoBiddingApi.get(profileId, campaignId, adProduct);
      if (!res.success) throw new Error(res.message || 'Failed to load auto bidding');
      return parseAutoBiddingSettings(res.data);
    },
    enabled: !!profileId && !!campaignId,
  });
  const previewQuery = useQuery({
    queryKey: ['auto-bidding-preview', profileId, campaignId, adProduct],
    queryFn: async () => {
      const res = await autoBiddingApi.preview(profileId, campaignId, adProduct);
      if (!res.success) throw new Error('Failed to load auto bidding preview');
      return parseAutoBiddingPreview(res.data);
    },
    enabled: !!profileId && !!campaignId,
  });
  const logsQuery = useQuery({
    queryKey: ['auto-bidding-run-logs', profileId, campaignId, adProduct],
    queryFn: async () => {
      const res = await autoBiddingApi.runLogs(profileId, campaignId, adProduct);
      if (!res.success) throw new Error('Failed to load auto bidding history');
      return parseAutoBiddingRunLogs(res.data);
    },
    enabled: !!profileId && !!campaignId,
  });

  function refreshAutoBidding() {
    void queryClient.invalidateQueries({
      queryKey: ['auto-bidding-settings', profileId, campaignId, adProduct],
    });
    void queryClient.invalidateQueries({
      queryKey: ['auto-bidding-preview', profileId, campaignId, adProduct],
    });
    void queryClient.invalidateQueries({
      queryKey: ['auto-bidding-run-logs', profileId, campaignId, adProduct],
    });
    void queryClient.invalidateQueries({
      queryKey: ['workspace-entity-page', 'campaigns', profileId],
    });
  }

  return (
    <>
      <AutoBiddingActivityPanelView
        settings={settingsQuery.data ?? null}
        settingsLoading={settingsQuery.isLoading}
        settingsError={settingsQuery.error ? (settingsQuery.error as Error).message : null}
        onConfigure={() => setConfigOpen(true)}
        preview={previewQuery.data ?? emptyAutoBiddingPreview()}
        previewLoading={previewQuery.isLoading}
        previewError={previewQuery.error ? (previewQuery.error as Error).message : null}
        logs={logsQuery.data ?? []}
        logsLoading={logsQuery.isLoading}
        logsError={logsQuery.error ? (logsQuery.error as Error).message : null}
      />
      {configOpen ? (
        <WorkspaceAutoBiddingModal
          profileId={profileId}
          campaignId={campaignId}
          campaignName={campaignName || campaignId}
          adProduct={adProduct}
          onClose={() => setConfigOpen(false)}
          onChanged={refreshAutoBidding}
        />
      ) : null}
    </>
  );
}
